import {useRef,useState} from 'react';
import {exportRoster,previewRosterImport,importRoster} from '../services/rosterTransfer';

// Roster export/import (Bots screen). Export downloads the full-roster
// transfer bundle; import runs file → parse → plan → preview → confirm —
// count-before-commit is a hard requirement (frontend.md §3 data-import
// pattern), and the merge is ADD-only: live bots/teams are never overwritten
// (planRosterImport skips existing slugs and reports them).
//
// No confidential data is involved: bot/team definitions are shared tenant
// infra; provider credentials stay in Dexie and never enter a bundle.

const initialState={
  stage: 'idle', // 'idle' | 'preview' | 'importing'
  fileName: null,
  parsed: null,
  plan: null
};

export function RosterTransferPanel({onError,onNotice}) {
  const [state,setState]=useState(initialState);
  const fileInput=useRef(null);

  function reset() {
    setState(initialState);
    if (fileInput.current) fileInput.current.value='';
  }

  function onExport() {
    try {
      const bundle=exportRoster();
      onNotice(
        `Exported ${bundle.bots.length} bots across ${bundle.teams.length} teams. Provider keys and chat history are never included.`
      );
    } catch (exportError) {
      onError(exportError.message);
    }
  }

  async function onFile(event) {
    const file=event.target.files?.[0];
    if (!file) return;

    try {
      const text=await file.text();
      const {parsed,plan}=previewRosterImport(text);
      setState({stage: 'preview',fileName: file.name,parsed,plan});
    } catch (previewError) {
      reset();
      onError(previewError.message);
    }
  }

  async function onConfirmImport() {
    setState((current)=> ({...current,stage: 'importing'}));

    try {
      const {parsed,plan}=state;
      const result=await importRoster({parsed,plan});

      const skipped=[
        result.skippedTeams.length ? `${result.skippedTeams.length} team(s)` : null,
        result.skippedBots.length ? `${result.skippedBots.length} bot(s)` : null
      ].filter(Boolean).join(' + ');

      onNotice(
        `Imported ${result.importedTeams} team(s) and ${result.importedBots} bot(s)`
        + (skipped ? ` — skipped ${skipped} that already exist here (nothing was overwritten).` : '.')
        + (result.notes.length ? ` Notes: ${result.notes.join(' ')}` : '')
      );
      reset();
    } catch (importError) {
      reset();
      onError(importError.message);
    }
  }

  const {stage,fileName,plan}=state;

  return (
    <div className="roster-transfer">
      <div className="picker-actions">
        <button className="button ghost compact" onClick={onExport}>
          Export bots
        </button>
        <button
          className="button ghost compact"
          onClick={()=> fileInput.current?.click()}
          disabled={stage==='importing'}
        >
          Import bots
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          onChange={onFile}
          style={{display: 'none'}}
        />
      </div>

      {stage==='preview' && plan && (
        <div className="picker-form">
          <b>Import preview — {fileName}</b>
          <p className="empty-copy">
            Adds {plan.newBots.length} bot(s) and {plan.newTeams.length} team(s) to this workspace.
            Existing bots and teams are never overwritten — anything with a slug
            that already exists here is skipped.
          </p>
          {plan.newTeams.length > 0 && (
            <ul className="transfer-list">
              {plan.newTeams.map((team)=> (
                <li key={team.slug}>
                  <b>{team.name}</b> <small>(new team · {team.module})</small>
                </li>
              ))}
            </ul>
          )}
          {plan.newBots.length > 0 && (
            <ul className="transfer-list">
              {plan.newBots.map((bot)=> (
                <li key={bot.slug}>
                  <b>{bot.name}</b> — {bot.role}
                  <small>
                    {bot.team ? `joins ${bot.team}` : 'lone specialist'}
                    {bot.is_lead ? ' · Lead' : ''}
                  </small>
                </li>
              ))}
            </ul>
          )}
          {(plan.skippedTeams.length > 0 || plan.skippedBots.length > 0) && (
            <p className="empty-copy">
              Skipping {plan.skippedTeams.length + plan.skippedBots.length} item(s) that already
              exist here: {[...plan.skippedTeams,...plan.skippedBots].map((item)=> item.name).join(', ')}.
            </p>
          )}
          {plan.notes.length > 0 && (
            <p className="empty-copy">{plan.notes.join(' ')}</p>
          )}
          <div className="picker-actions">
            <button
              className="button primary compact"
              onClick={onConfirmImport}
              disabled={stage==='importing' || (!plan.newBots.length && !plan.newTeams.length)}
            >
              {stage==='importing' ? 'Importing…' : 'Add to my bots'}
            </button>
            <button className="button ghost compact" onClick={reset} disabled={stage==='importing'}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
